import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from "react";
import { authService } from "@/services/authService";
import ApiUser from "@/model/interface/api/ApiUser";
import { useLazyGetHomeQuery } from "@/store/service";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";

interface AuthContextType {
  isAuthenticated: boolean;
  authHydrated: boolean;
  user: ApiUser | null;
  logout: () => Promise<void>;
  refreshAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authHydrated, setAuthHydrated] = useState(false);

  const [fetchUser, { data: userData }] = useLazyGetHomeQuery();

  const refreshAuthStatus = useCallback(async () => {
    const authenticated = await authService.checkAuthStatus();
    setIsAuthenticated(authenticated);

    if (authenticated) {
      await fetchUser();
    }
  }, [fetchUser]);

  const logout = useCallback(async () => {
    setIsAuthenticated(false);
    await authService.logout();
  }, []);

  useIdleTimeout(logout, isAuthenticated);

  useEffect(() => {
    (async () => {
      await refreshAuthStatus();
      setAuthHydrated(true);
    })();

    const handleAuthRefreshed = () => {
      setIsAuthenticated(true);
    };

    const handleAuthLogout = async () => {
      setIsAuthenticated(false);
      await authService.logout();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshAuthStatus();
      }
    };

    window.addEventListener("auth-refreshed", handleAuthRefreshed);
    window.addEventListener("auth-logout", handleAuthLogout);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("auth-refreshed", handleAuthRefreshed);
      window.removeEventListener("auth-logout", handleAuthLogout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshAuthStatus]);

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated,
      authHydrated,
      user: userData || null,
      logout,
      refreshAuthStatus,
    }),
    [isAuthenticated, authHydrated, userData, logout, refreshAuthStatus],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
